import { ErrorSummary } from '@digdir/designsystemet-react';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';

type Result = {
  field: string;
  message: string | undefined;
  type: string | undefined;
  fieldId: string | undefined;
}[];

const anchorFromFieldErrorRef = (ref: unknown): string | undefined => {
  if (ref && typeof ref === 'object' && 'id' in ref) {
    const id = (ref as { id?: string }).id;
    return typeof id === 'string' && id.length > 0 ? id : undefined;
  }
  return undefined;
};

const findErrorsRecursively = (obj: Record<string, any>, result: Result = []) => {
  for (const key in obj) {
    const value = obj?.[key];

    if (typeof value !== 'object' || value === null) {
      continue;
    }

    if (typeof value === 'object' && (value?.type || value?.message)) {
      const refAnchorId = typeof value?.refAnchorId === 'string' ? value.refAnchorId : undefined;
      result.push({
        field: key,
        message: value?.message,
        type: value?.type,
        fieldId: refAnchorId ?? anchorFromFieldErrorRef(value?.ref)
      });
    } else {
      // Continue recursively for nested objects
      findErrorsRecursively(value, result);
    }
  }

  return result;
};

interface Props {
  errors: Record<string, any>;
  /**
   * Field name → DOM id for the error link, when `ref` does not have a useful id (e.g. for hidden inputs – `#id` scrolling often doesn't work).
   * Must match an existing element with that id (for example, a wrapper around the map).
   */
  fieldAnchorIds?: Record<string, string>;
}

// Common form error component to handle React Hook Form errors
export const FormErrors: FC<Props> = ({ errors, fieldAnchorIds }) => {
  const { t } = useTranslation();

  if (Object.keys(errors).length === 0) {
    return null;
  }

  // Merge object errors and array errors into a single array
  const mergedErrors = findErrorsRecursively(errors);

  return (
    <ErrorSummary>
      <ErrorSummary.Heading>{t('form.errors.heading')}</ErrorSummary.Heading>
      <ErrorSummary.List>
        {
          //Iterate through errors and display them
          mergedErrors.map((item, index) => {
            const anchorId = fieldAnchorIds?.[item.field] ?? item.fieldId;
            return (
              <ErrorSummary.Item key={`${item.field}-${index}`}>
                {anchorId ? (
                  <ErrorSummary.Link href={`#${anchorId}`}>{item.message}</ErrorSummary.Link>
                ) : (
                  item.message
                )}
              </ErrorSummary.Item>
            );
          })
        }
      </ErrorSummary.List>
    </ErrorSummary>
  );
};
